<template>
    <div class="agent-table-container">
      <ve-table
        max-height="calc(100vh - 35rem)"
        :fixed-header="true"
        :columns="columns"
        :table-data="tableData"
      />
      <div v-if="isLoading" class="agents-loader">
        <spinner />
        <span>{{
          $t('OVERVIEW_REPORTS.AGENT_CONVERSATIONS.LOADING_MESSAGE')
        }}</span>
      </div>
      <empty-state
        v-else-if="!isLoading && !agentMetrics.length"
        :title="$t('OVERVIEW_REPORTS.LABEL_TABLE.NO_AGENTS')"
      />
      <div class="table-pagination">
        <ve-pagination
          :total="labelCount"
          :page-index="pageIndex"
          :page-size="10"
          :page-size-option="[10]"
          @on-page-number-change="onPageNumberChange"
        />
      </div>
    </div>
  </template>
  
  <script>
  import { VeTable, VePagination } from 'vue-easytable';
  import Spinner from 'shared/components/Spinner.vue';
  import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
  import rtlMixin from 'shared/mixins/rtlMixin';
  import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue';
import message from '../../../../api/inbox/message';
import timeMixin from 'dashboard/mixins/time';
  
  export default {
    name: 'LabelTable',
    components: {
      EmptyState,
      Spinner,
      VeTable,
      VePagination,
    },
    mixins: [rtlMixin, timeMixin],
    props: {
      agentMetrics: {
        type: Object,
        default: () => [],
      },
      isLoading: {
        type: Boolean,
        default: false,
      },
      pageIndex: {
        type: Number,
        default: 1,
      },
    },
    computed: {
      labelCount() {
        return this.agentMetrics?.data?.get_labels_count
      },
      labelTable() {
        
        return this.agentMetrics?.data?.label_table
      },
      tableData() {
        return this.agentMetrics?.data?.label_table.map(agent => {
          const messageLink = `${window.chatwootConfig.hostURL}/app/accounts/${agent.account_id}/conversations/${agent.display_id}`;
          const date = new Date(agent.message_created).getTime();
          return {
            contact: agent.contact_name,
            agent: agent.assignee_name,
            createdAgo: this.dynamicTime(date/1000),
            createdAt: this.messageStamp(date/1000, 'LLL d yyyy, h:mm a'),
            status: agent.status === 'resolved' ? this.$t(
            'OVERVIEW_REPORTS.LABEL_TABLE.SOLVED'
          ) : this.$t(
            'OVERVIEW_REPORTS.LABEL_TABLE.NOT_SOLVED'
          ),
            link: messageLink,
            message_id: agent.display_id,
            inbox: agent.inbox_name,
          };
        });
      },
      columns() {
        return [
          {
            field: 'contact',
            key: 'contact',
            title: this.$t(
            'OVERVIEW_REPORTS.LABEL_TABLE.TABLE_HEADER.CONTACT'
          ),
            fixed: 'left',
            align: this.isRTLView ? 'right' : 'left',
            width: 10,
            renderBodyCell: ({ row }) => (
              <div class="row-user-block">
                <Thumbnail
                  src={''}
                  size="32px"
                  username={row.contact ? row.contact : "---"}
                  status={row.status}
                />
                <div class="user-block">
                  <h6 class="title text-truncate">{row.contact ? row.contact : "---"}</h6>
                </div>
              </div>
            ),
          },
          {
            field: 'agent',
            key: 'agent',
            title: this.$t(
            'OVERVIEW_REPORTS.LABEL_TABLE.TABLE_HEADER.AGENT'
          ),
            align: this.isRTLView ? 'right' : 'left',
            width: 10,
            renderBodyCell: ({ row }) => (
              <div class="row-user-block">
                <Thumbnail
                  src={''}
                  size="32px"
                  username={row.agent ? row.agent : "---"}
                  status={row.status}
                />
                <div class="user-block">
                  <h6 class="title text-truncate">{row.agent ? row.agent : "---"}</h6>
                </div>
              </div>
            ),
          },
          {
            field: 'inbox',
            key: 'inbox',
            title: this.$t(
            'OVERVIEW_REPORTS.LABEL_TABLE.TABLE_HEADER.INBOX'
          ),
            align: this.isRTLView ? 'right' : 'left',
            width: 10,
          },
          {
            field: 'status',
            key: 'status',
            title: this.$t(
            'OVERVIEW_REPORTS.LABEL_TABLE.TABLE_HEADER.STATUS'
          ),
            align: this.isRTLView ? 'right' : 'left',
            width: 10,
          },
          {
            field: 'link',
            key: 'link',
            title: this.$t(
            'OVERVIEW_REPORTS.LABEL_TABLE.TABLE_HEADER.LINK'
          ),
            align: this.isRTLView ? 'right' : 'left',
            width: 10,
            renderBodyCell: ({ row }) => (
              <div class="text-right">
                <a href={row.link}>#{row.message_id}</a>
                <div class="csat--timestamp" v-tooltip={row.createdAt}>
                  {row.createdAgo}
                </div>
              </div>
            ),
          },
        ];
      },
    },
    methods: {
      onPageNumberChange(pageIndex) {
        this.$emit('page-change', pageIndex);
      },
    },
  };
  </script>
  
  <style lang="scss" scoped>
  .agent-table-container {
    display: flex;
    margin-top: 10px;
    margin-bottom: 10px;
    flex-direction: column;
    flex: 1;
  
    .ve-table {
      &::v-deep {
        th.ve-table-header-th {
          font-size: var(--font-size-mini) !important;
          padding: var(--space-small) var(--space-two) !important;
        }
  
        td.ve-table-body-td {
          padding: var(--space-one) var(--space-two) !important;
        }
      }
    }
  
    &::v-deep .ve-pagination {
      background-color: transparent;
    }
  
    &::v-deep .ve-pagination-select {
      display: none;
    }
  
    .row-user-block {
      align-items: center;
      display: flex;
      text-align: left;
  
      .user-block {
        align-items: flex-start;
        display: flex;
        flex-direction: column;
        min-width: 0;
        margin: 0 var(--space-small);
  
        .title {
          font-size: var(--font-size-small);
          margin: var(--zero);
          line-height: 1.2;
        }
        .sub-title {
          font-size: var(--font-size-mini);
        }
      }
    }
  
    .table-pagination {
      margin-top: var(--space-normal);
      text-align: right;
    }
  }
  
  .agents-loader {
    align-items: center;
    display: flex;
    font-size: var(--font-size-default);
    justify-content: center;
    padding: var(--space-large);
  }
  .csat--timestamp {
  color: var(--b-400);
  font-size: var(--font-size-small);
}
  </style>
  